import type { ReactNode } from 'react'
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react'
import type { HubConnection } from '@microsoft/signalr'
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import {
	getServiceHostAndEndpointPath,
	getAccessToken
} from 'lib/interceptor/axios.interceptor'
import type { IUserProfile } from 'models/userProfiles'
import { useLoggedInUserProfileContext } from 'lib/contexts/loggedInUserProfile.context'

interface RealtimeContextType {
	initializeHub: (hubName: string, userProfileId: string) => void
}

export const RealtimeContext = React.createContext<RealtimeContextType | null>(
	null
)

export const HUB_USERPROFILE = 'user-profile'
const MAX_RETRIES = 3
const BASE_RETRY_INTERVAL = 5000

interface RealtimeProviderProperties {
	children: ReactNode
}

export const RealtimeProvider: React.FunctionComponent<
	RealtimeProviderProperties
> = ({ children }) => {
	const { setUserProfile, userProfile } = useLoggedInUserProfileContext()
	const [hubConnection, setHubConnection] = useState<HubConnection | null>(null)
	const [initializedHubs, setInitializedHubs] = useState<Set<string>>(new Set())

	const startConnection = async (connection: HubConnection): Promise<void> => {
		let attempt = 0
		const connect = async (): Promise<void> => {
			try {
				console.log('SignalR connecting')
				await connection.start()
			} catch (error) {
				attempt += 1
				const retryInterval = BASE_RETRY_INTERVAL * attempt
				console.log(
					`Attempt ${attempt}: ${JSON.stringify(error)}. Retrying in ${retryInterval / 1000} seconds.`
				)
				if (attempt < MAX_RETRIES) {
					setTimeout(connect, retryInterval)
				} else {
					console.log('Failed to connect after maximum attempts')
				}
			}
		}
		await connect()
	}

	const initializeHub = useCallback(
		(hubName: string, userProfileId: string) => {
			const hubIdentifier = `${hubName}-${userProfileId}`
			if (initializedHubs.has(hubIdentifier)) {
				console.log('Hub already initialized:', hubIdentifier)
				return
			}

			setInitializedHubs(previous => new Set(previous).add(hubIdentifier))

			const hubUrl = getServiceHostAndEndpointPath(
				`/rtw/api/${hubName}/${userProfileId}`
			)

			const connection = new HubConnectionBuilder()
				.withUrl(`${hubUrl.host}${hubUrl.endpointPath}`, {
					accessTokenFactory: async () => {
						const token = await getAccessToken()
						return token ?? ''
					}
				})
				.configureLogging(LogLevel.Information)
				.build()

			connection.on(
				'update',
				async (message: { userProfile: IUserProfile }) => {
					const {
						associatedVenues,
						isPaidSubscriber,
						licenseInfo,
						organizationId,
						organizationName,
						role,
						disabled,
						isDisabled,
						disabledReason,
						accountType,
						venueMatchCriteria,
						permissions,
						organizationRoles,
						previewExperiences
					} = message.userProfile

					setUserProfile(
						previousUserProfile =>
							({
								...previousUserProfile,
								associatedVenues,
								isPaidSubscriber,
								licenseInfo,
								licenseTier: licenseInfo?.tier ?? null,
								organizationId,
								organizationName,
								role,
								disabled,
								isDisabled,
								disabledReason,
								accountType,
								venueMatchCriteria,
								permissions,
								organizationRoles,
								previewExperiences
							}) as IUserProfile
					)
				}
			)

			connection.onclose(async error => {
				console.error('Disconnected, retrying connection:', error)
				await startConnection(connection)
			})

			startConnection(connection)
				.then(() => console.log('SignalR Connected'))
				.catch(() => console.error('Failed to start the connection'))

			setHubConnection(connection)
		},
		[initializedHubs, setUserProfile]
	)

	useEffect(() => {
		if (hubConnection) {
			console.info(`hubConnection.connectionState = ${hubConnection.state}`)
		} else {
			console.info('hubConnection is null')
		}
	}, [hubConnection])

	useEffect(() => {
		if (userProfile?.id) {
			initializeHub(HUB_USERPROFILE, userProfile.id)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userProfile?.id])

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const value = useMemo(() => ({ initializeHub }), [])

	return (
		<RealtimeContext.Provider value={value}>
			{children}
		</RealtimeContext.Provider>
	)
}

export const useRealtimeContext = (): RealtimeContextType => {
	const context = useContext(RealtimeContext)
	if (!context) {
		throw new Error('useRealtimeContext must be used within a RealtimeProvider')
	}
	return context
}
